import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePacientes } from '../../hooks/usePacientes';
import { createConsultation, getConsultasProximosDias } from '../../services/dataService';
import { openGoogleCalendar, createConsultationEvent } from '../../services/googleCalendarService';
import { scheduleConsultationNotifications } from '../../services/notificationScheduler';
import './Consultation.css';

interface CreateConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateConsultationModal: React.FC<CreateConsultationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { pacientes, loading: loadingPacientes } = usePacientes();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    patientId: '',
    date: '',
    time: '',
    type: 'retorno' as 'primeira' | 'retorno',
    status: 'confirmada' as 'confirmada' | 'aguardando',
    notes: '',
    valor: '',
    pago: false,
    dataPagamento: '',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        patientId: '',
        date: '',
        time: '',
        type: 'retorno',
        status: 'confirmada',
        notes: '',
        valor: '',
        pago: false,
        dataPagamento: '',
      });
      setError(null);
    } else {
      // Set default date to today
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, date: dateStr }));
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError('Usuário não autenticado');
      return;
    }

    if (!formData.patientId) {
      setError('Selecione um paciente');
      return;
    }

    if (!formData.date || !formData.time) {
      setError('Preencha data e horário');
      return;
    }

    setSaving(true);
    try {
      const dataIso = new Date(`${formData.date}T${formData.time}`).toISOString();
      
      await createConsultation(user.uid, formData.patientId, {
        dataIso,
        tipo: formData.type,
        status: formData.status,
        notes: formData.notes || undefined,
        valor: formData.valor ? parseFloat(formData.valor.replace(',', '.')) : undefined,
        pago: formData.pago || undefined,
        dataPagamento: formData.dataPagamento || undefined,
      });

      // Agendar notificações para a consulta (buscar ID após criar)
      try {
        const consultas = await getConsultasProximosDias(user.uid, 30);
        const novaConsulta = consultas.find(c => 
          c.dataIso === dataIso && 
          c.pacienteId === formData.patientId
        );
        
        if (novaConsulta) {
          await scheduleConsultationNotifications(novaConsulta, user.uid);
        }
      } catch (error) {
        console.error('Erro ao agendar notificações:', error);
      }

      // Opção de adicionar ao Google Calendar após criar
      const paciente = pacientes.find(p => p.id === formData.patientId);
      if (paciente && window.confirm('Deseja adicionar esta consulta ao Google Calendar?')) {
        const event = createConsultationEvent(
          paciente.nome,
          formData.type,
          dataIso,
          formData.notes
        );
        openGoogleCalendar(event);
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('Erro ao criar consulta:', err);
      setError(err.message || 'Erro ao agendar consulta. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova Consulta"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Agendando...' : 'Agendar Consulta'}
          </Button>
        </>
      }
    >
      <form className="ns-consultation-form" onSubmit={handleSubmit}>
        {error && <div className="ns-form-error">{error}</div>}

        <label className="ns-field">
          <span>Paciente *</span>
          <select
            className="ns-input"
            value={formData.patientId}
            onChange={(e) =>
              setFormData({ ...formData, patientId: e.target.value })
            }
            disabled={loadingPacientes || saving}
            required
          >
            <option value="">Selecione um paciente</option>
            {pacientes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome} {p.email ? `(${p.email})` : ''}
              </option>
            ))}
          </select>
        </label>

        <div className="ns-form-row">
          <label className="ns-field">
            <span>Data *</span>
            <input
              className="ns-input"
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              min={new Date().toISOString().split('T')[0]}
              disabled={saving}
              required
            />
          </label>

          <label className="ns-field">
            <span>Horário *</span>
            <input
              className="ns-input"
              type="time"
              value={formData.time}
              onChange={(e) =>
                setFormData({ ...formData, time: e.target.value })
              }
              disabled={saving}
              required
            />
          </label>
        </div>

        <div className="ns-form-row">
          <label className="ns-field">
            <span>Tipo de consulta *</span>
            <select
              className="ns-input"
              value={formData.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  type: e.target.value as 'primeira' | 'retorno',
                })
              }
              disabled={saving}
              required
            >
              <option value="retorno">Retorno</option>
              <option value="primeira">Primeira consulta</option>
            </select>
          </label>

          <label className="ns-field">
            <span>Status *</span>
            <select
              className="ns-input"
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as 'confirmada' | 'aguardando',
                })
              }
              disabled={saving}
              required
            >
              <option value="confirmada">Confirmada</option>
              <option value="aguardando">Aguardando confirmação</option>
            </select>
          </label>
        </div>

        <label className="ns-field">
          <span>Observações</span>
          <textarea
            className="ns-textarea"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Observações sobre a consulta (opcional)"
            rows={3}
            disabled={saving}
          />
        </label>

        <div className="ns-form-row">
          <label className="ns-field">
            <span>Valor da Consulta (R$)</span>
            <input
              className="ns-input"
              type="text"
              value={formData.valor}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d,.-]/g, '');
                setFormData({ ...formData, valor: value });
              }}
              placeholder="0,00"
              disabled={saving}
            />
          </label>

          <label className="ns-field">
            <span>Status de Pagamento</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <input
                type="checkbox"
                checked={formData.pago}
                onChange={(e) =>
                  setFormData({ ...formData, pago: e.target.checked })
                }
                disabled={saving}
              />
              <span>Marcar como pago</span>
            </div>
          </label>
        </div>

        {formData.pago && (
          <label className="ns-field">
            <span>Data de Pagamento</span>
            <input
              className="ns-input"
              type="date"
              value={formData.dataPagamento}
              onChange={(e) =>
                setFormData({ ...formData, dataPagamento: e.target.value })
              }
              disabled={saving}
            />
          </label>
        )}
      </form>
    </Modal>
  );
};

